import { useRef, useState, useEffect } from "react";
import {
  IconButton,
  MenuItem,
  Divider,
  ListItemText,
  ClickAwayListener,
  Fade,
  Paper,
  Popper,
  MenuList,
  Typography,
} from "@mui/material";
import { CloseIconFill, MoreVertIcon, InfoIcon } from "@/ImageRepo";
import "./ThreeDotSettingMenu.css";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchConnectionPairs,
  fetchGlobalProfile,
  selectGlobalProfile,
  selectConnectionPairDetails,
  setSelectedConnectionPairs,
  setIsWorkflowSettingsOpen,
} from "@/features/settings/settingsSlice";
import {
  setViewDataPopupOpen,
  fetchViewDataPopupData,
  selectConnection,
  selectDateFilter,
  getWorkflows,
} from "@/features/homepage/homeSlice";
import { selectWorkflow, statusTypes } from "@/features/homepage/workflowManagerSessionSlice";
import { useGridApiContext } from "@mui/x-data-grid-pro";
import axios from "@/features/axiosHelper";
import { fetchRecordsData } from "@/features/homepage/workflowManagerSlice";
import { createAlert } from "@/features/homepage/alertSliceList";
import dayjs from "dayjs";

const ThreeDotSettingMenu = ({ getSelectedOption, taskType, rowData, setLastRefreshedAt }) => {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);
  const dispatch = useDispatch();
  const apiRef = useGridApiContext();

  const activeOptions = {
    Process: 1,
    Hold: 1,
    Unhold: 1,
    Requeue: 1,
    Archive: 1,
    Restore: 1,
    "View Source": 1,
    Catalog: 1,
    "Workflow Settings": 1,
    "View Data": 1,
  };

  const currentOperationId = useSelector(selectWorkflow);
  const connPairDetails = useSelector(selectConnectionPairDetails);
  const selectedWorkflow = useSelector(selectWorkflow);
  const currentDateFilter = useSelector(selectDateFilter);
  const currentConnectionId = useSelector(selectConnection);
  const globalProfile = useSelector(selectGlobalProfile);

  const getRecordsData = (clearCache = true) => {
    let startDate = currentDateFilter.startDate;
    let endDate = currentDateFilter.endDate;
    const defaultStartDays = 7;

    if (startDate === "" && endDate === "") {
      startDate = dayjs().subtract(defaultStartDays, "day").startOf("day");
      endDate = dayjs();
    } else if (endDate === null) {
      endDate = startDate;
    }
    endDate = endDate.add(1, "day"); // always set to end of day
    dispatch(
      fetchRecordsData({
        operationId: currentOperationId,
        startDate: startDate,
        endDate: endDate,
        connectionId:
          currentConnectionId === "All" ? -1 : currentConnectionId.ConnectionId,
        clearCache: clearCache,
      })
    );
    if (setLastRefreshedAt) {
      setLastRefreshedAt(dayjs());
    }
    dispatch(getWorkflows());
  };

  useEffect(() => {
    if (
      open &&
      (connPairDetails === undefined || connPairDetails.length === 0)
    ) {
      dispatch(fetchConnectionPairs({ clearCache: true }));
      dispatch(fetchGlobalProfile(selectedWorkflow));
    }
  }, [open]);

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const validateRequeue = (event) => {
    if (rowData.status === statusTypes.Successful || rowData.status === statusTypes.CompletedWithWarning) {
      dispatch(
        createAlert({
          status: "Warning",
          alertType: "result",
          message:
            "Requeueing success records will archive current ones and create duplicate records. Do you wish to continue?",
          buttonLabel: "Requeue",
          onButtonClick: () => handleRequeueClick(event),
          buttonLabel2: "Cancel",
        })
      );
    } else {
      dispatch(
        createAlert({
          status: "Warning",
          alertType: "result",
          message: "Are you sure you want to Requeue the record?",
          buttonLabel: "Requeue",
          onButtonClick: () => handleRequeueClick(event),
          buttonLabel2: "Cancel",
        })
      );
    }
  };

  const handleRequeueClick = async (event) => {
    event.stopPropagation();
    if (activeOptions["Requeue"] !== 1) return;

    // heap.io
    window.heap.track("Workflow - Requeue Record");

    const workflowRowActionDTO = [
      {
        SourceDictionaryDataId: rowData.sourceArtifactDictionaryDataId,
        Status: rowData.status,
        IsHeld: rowData.isHeld,
        IsArchived: rowData.isArchived,
        SourceReference: rowData.fromReference,
        OperationId: currentOperationId,
        SourceConnectionId: rowData.sourceConnectionId,
        ConnPairId: rowData.connectionPairId,
        WorkflowRecordId: rowData.id,
      },
    ];

    // This has been taken from the operations manager, but the manager was only configured to run operation tasks and no execution path went to Endpoint Tasks
    if (true /*taskType === task.OperationTask*/) {
      axios
        .post(
          "/operationsmanager/requeue-operation-task",
          workflowRowActionDTO,
          null
        )
        .then((response) => {
          if (response.data) {
            dispatch(
              createAlert({
                status: response.data.alert,
                alertType: "single",
                message: response.data.message,
              })
            );
            getRecordsData();
          }
        })
        .catch((error) => {
          dispatch(
            createAlert({
              status: "Error",
              alertType: "single",
              message: error.response.data,
            })
          );
          console.error("Error applying option: ", error);
        });
    } else if (true /*taskType === task.EndpointTask*/) {
      axios
        .post("/operationsmanager/requeue-endpoint-task", null, {
          params: {
            targetReferenceId: rowData.targetArtifactDictionaryDataId,
            targetConnectionId: rowData.tar,
          },
        })
        .then((response) => {
          if (response.data.length > 0) {
            alert(response.data);
          }
        })
        .catch((error) => {
          console.error("Error applying option: ", error);
        });
    }

    handleClose(event);
  };

  const validateHold = (event) => {
    dispatch(
      createAlert({
        status: "Warning",
        alertType: "result",
        message: "Are you sure you want to Hold the record?",
        buttonLabel: "Hold",
        onButtonClick: () => handleHoldClick(event),
        buttonLabel2: "Cancel",
      })
    );
  };

  const handleHoldClick = (event) => {
    event.stopPropagation();
    if (activeOptions["Hold"] !== 1) return;

    // heap.io
    window.heap.track("Workflow - Hold Record");

    const workflowRowDTO = [
      {
        SourceDictionaryDataId: rowData.sourceArtifactDictionaryDataId,
        Status: rowData.status,
        IsHeld: rowData.isHeld,
        IsArchived: rowData.isArchived,
        SourceReference: rowData.fromReference,
      },
    ];

    axios
      .post("/operationsmanager/hold-workflows", workflowRowDTO, null)
      .then((response) => {
        if (response.data) {
          dispatch(
            createAlert({
              status: response.data.alert,
              alertType: "single",
              message: response.data.message,
            })
          );
          getRecordsData();
        }
      })
      .catch((error) => {
        dispatch(
          createAlert({
            status: "Error",
            alertType: "single",
            message: error.response.data,
          })
        );
        console.error("Error applying option: ", error);
      });

    handleClose(event);
  };

  const validateUnhold = (event) => {
    dispatch(
      createAlert({
        status: "Warning",
        alertType: "result",
        message: "Are you sure you want to Unhold the record?",
        buttonLabel: "Unhold",
        onButtonClick: () => handleUnholdClick(event),
        buttonLabel2: "Cancel",
      })
    );
  };

  const handleUnholdClick = (event) => {
    event.stopPropagation();
    if (activeOptions["Unhold"] !== 1) return;

    // heap.io
    window.heap.track("Workflow - Unhold Record");

    const workflowRowDTO = [
      {
        SourceDictionaryDataId: rowData.sourceArtifactDictionaryDataId,
        Status: rowData.status,
        IsHeld: rowData.isHeld,
        IsArchived: rowData.isArchived,
        SourceReference: rowData.fromReference,
      },
    ];

    axios
      .post("/operationsmanager/unhold-workflows", workflowRowDTO, null)
      .then((response) => {
        if (response.data) {
          dispatch(
            createAlert({
              status: response.data.alert,
              alertType: "single",
              message: response.data.message,
            })
          );
          getRecordsData();
        }
      })
      .catch((error) => {
        createAlert({
          status: "Error",
          alertType: "single",
          message: error.response.data,
        });
        console.error("Error applying option: ", error);
      });

    handleClose(event);
  };

  const validateArchive = (event) => {
    dispatch(
      createAlert({
        status: "Warning",
        alertType: "result",
        message: "Are you sure you want to Archive the record?",
        buttonLabel: "Archive",
        onButtonClick: () => handleArchiveClick(event),
        buttonLabel2: "Cancel",
      })
    );
  };

  const handleArchiveClick = (event) => {
    event.stopPropagation();
    if (activeOptions["Archive"] !== 1) return;

    // heap.io
    window.heap.track("Workflow - Archive Record");

    const workflowRowDTO = [
      {
        SourceDictionaryDataId: rowData.sourceArtifactDictionaryDataId,
        Status: rowData.status,
        IsHeld: rowData.isHeld,
        IsArchived: rowData.isArchived,
        SourceReference: rowData.fromReference,
      },
    ];

    axios
      .post("/operationsmanager/archive-workflows", workflowRowDTO, null)
      .then((response) => {
        if (response.data) {
          dispatch(
            createAlert({
              status: response.data.alert,
              alertType: "single",
              message: response.data.message,
            })
          );
          getRecordsData();
        }
      })
      .catch((error) => {
        dispatch(
          createAlert({
            status: "Error",
            alertType: "single",
            message: error.response.data,
          })
        );
        console.error("Error applying option: ", error);
      });

    handleClose(event);
  };

  const validateProcess = (event) => {
    let enabledWorkflowExists = false;

    try {
      const filteredConnPairs = connPairDetails.filter(
        (connPair) =>
          (connPair.ConnectionId1 === rowData.sourceConnectionId ||
            connPair.ConnectionId2 === rowData.sourceConnectionId) &&
          Array.isArray(connPair.ConnectionProfiles) &&
          connPair.ConnectionProfiles.length > 0
      );

      for (const connPair of filteredConnPairs) {
        const matchingProfiles = connPair.ConnectionProfiles.filter(
          (profile) => profile.OperationId === selectedWorkflow
        );

        for (const profile of matchingProfiles) {
          const settings = JSON.parse(profile.JSONProperties);

          // Determine if global settings are used
          const useGlobal = settings.WorkflowSchedule?.UseGlobalDisabled;
          const isEnabled = useGlobal
            ? globalProfile.WorkflowSchedule?.WorkflowDisabled === false
            : settings.WorkflowSchedule?.WorkflowDisabled === false;

          if (isEnabled) {
            enabledWorkflowExists = true;
            break;
          }
        }
        if (enabledWorkflowExists) break;
      }

      if (enabledWorkflowExists) {
        dispatch(
          createAlert({
            status: "Warning",
            alertType: "result",
            message:
              "This record will automatically be processed by scheduled automation, are you sure you want to process the record now?",
            buttonLabel: "Process",
            onButtonClick: () => handleProcessClick(event),
            buttonLabel2: "Cancel",
          })
        );
      } else {
        dispatch(
          createAlert({
            status: "Warning",
            alertType: "result",
            message: "Are you sure you want to Process the record?",
            buttonLabel: "Process",
            onButtonClick: () => handleProcessClick(event),
            buttonLabel2: "Cancel",
          })
        );
      }
    } catch (error) {
      dispatch(
        createAlert({
          status: "Warning",
          alertType: "result",
          message: "Are you sure you want to Process the record?",
          buttonLabel: "Process",
          onButtonClick: () => handleProcessClick(event),
          buttonLabel2: "Cancel",
        })
      );
    }
  };

  const handleProcessClick = (event) => {
    event.stopPropagation();
    if (activeOptions["Process"] !== 1) return;

    // heap.io
    window.heap.track("Workflow - Process Record");

    const workflowRowActionDTO = [
      {
        SourceDictionaryDataId: rowData.sourceArtifactDictionaryDataId,
        Status: rowData.status,
        IsHeld: rowData.isHeld,
        IsArchived: rowData.isArchived,
        SourceReference: rowData.fromReference,
        OperationId: currentOperationId,
        SourceConnectionId: rowData.sourceConnectionId,
        ConnPairId: rowData.connectionPairId,
        WorkflowRecordId: rowData.id,
      },
    ];

    axios
      .post("/operationsmanager/process-workflow", workflowRowActionDTO, null)
      .then((response) => {
        if (response.data) {
          dispatch(
            createAlert({
              status: response.data.alert,
              alertType: "single",
              message: response.data.message,
            })
          );
          getRecordsData();
        }
      })
      .catch((error) => {
        dispatch(
          createAlert({
            status: "Error",
            alertType: "single",
            message: error.response.data,
          })
        );
        console.error("Error applying option: ", error);
      });

    handleClose(event);
  };

  const handleViewSourceClick = async (event) => {
    event.stopPropagation();
    if (activeOptions["View Source"] !== 1) return;

    // heap.io
    window.heap.track("Workflow - View Data");

    dispatch(
      fetchViewDataPopupData({
        viewType: "(Raw, Structured & Catalog)",
        sourceReferenceId: rowData.sourceArtifactDictionaryDataId,
        targetReferenceId: rowData.targetArtifactDictionaryDataId,
        operationId: currentOperationId,
        connectionPairId: rowData.connectionPairId,
      })
    );
    dispatch(setViewDataPopupOpen(true));

    handleClose(event);
  };

  const handleWorkflowSettingsClick = (event) => {
    event.stopPropagation();
    if (activeOptions["Workflow Settings"] !== 1) return;

    // heap.io
    window.heap.track("Workflow Settings - Open via 3-dot");

    let newConnection = connPairDetails.filter(
      (detail) =>
        (detail.ConnectionId1 === rowData.sourceConnectionId &&
          detail.ConnectionId2 === rowData.targetConnectionId) ||
        (detail.ConnectionId2 === rowData.sourceConnectionId &&
          detail.ConnectionId1 === rowData.targetConnectionId)
    );

    dispatch(
      setSelectedConnectionPairs({
        selectedConnectionPairs: newConnection,
      })
    );
    dispatch(setIsWorkflowSettingsOpen({ isOpen: true }));
    handleClose(event);
  };

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  // return focus to the button when we transitioned from !open -> open
  const prevOpen = useRef(open);
  useEffect(() => {
    if (prevOpen.current === true && open === false) {
      anchorRef.current.focus();
    }

    prevOpen.current = open;
  }, [open]);

  const isActive = (option) => {
    if (rowData.connectionPairId === -1 &&
      (option !== "Workflow Settings" &&
        option !== "View Data")) {
      return "inactive";
    } else if (
      rowData.isHeld &&
      (option === "Requeue" ||
        option === "Process" ||
        option === "Archive" ||
        option === "Hold")
    ) {
      return "inactive";
    } else if (
      rowData.isArchived &&
      (option === "Archive" || option === "Hold")
    ) {
      return "inactive";
    }
    if (!rowData.isHeld && option === "Unhold") {
      return "inactive";
    }
    if (rowData.status == statusTypes.NotQueued && option === "Requeue") {
      return "inactive";
    }
    return activeOptions[option] === 1 ? "active" : "inactive";
  };

  const validateRestore = (event) => {
    dispatch(
      createAlert({
        status: "Warning",
        alertType: "result",
        message: "Are you sure you want to Restore the record?",
        buttonLabel: "Restore",
        onButtonClick: () => handleRestoreClick(event),
        buttonLabel2: "Cancel",
      })
    );
  };
  const handleRestoreClick = (event) => {
    event.stopPropagation();
    if (activeOptions["Restore"] !== 1) return;

    // heap.io
    window.heap.track("Workflow - Restore Record");

    const workflowRowDTO = [
      {
        SourceDictionaryDataId: rowData.sourceArtifactDictionaryDataId,
        Status: rowData.status,
        IsHeld: rowData.isHeld,
        IsArchived: rowData.isArchived,
        SourceReference: rowData.fromReference,
      },
    ];

    axios
      .post("/operationsmanager/unarchive-workflows", workflowRowDTO, null)
      .then((response) => {
        if (response.data) {
          dispatch(
            createAlert({
              status: response.data.alert,
              alertType: "single",
              message: response.data.message,
            })
          );
          getRecordsData();
        }
      })
      .catch((error) => {
        dispatch(
          createAlert({
            status: "Error",
            alertType: "single",
            message: error.response.data,
          })
        );
        console.error("Error applying option: ", error);
      });

    handleClose(event);
  };

  return (
    <>
      <IconButton
        ref={anchorRef}
        onClick={handleToggle}
        style={{ padding: "0px" }}
        disableRipple
      >
        <MoreVertIcon className="more-vert-icon" />
      </IconButton>
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement="left-start"
        transition
        className={"three-dot-menu"}
        // disablePortal
        modifiers={[{ name: "flip", enabled: true }]}
      >
        {({ TransitionProps, placement }) => (
          <Fade {...TransitionProps} timeout={500}>
            <Paper
              sx={{
                borderRadius: "6px",
              }}
            >
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList
                  autoFocusItem={open}
                  sx={{
                    borderRadius: "6px",
                    padding: "8px 6px 8px 6px !important",
                    fontFamily: "var(--font-family)",
                  }}
                >
                  <CloseIconFill onClick={handleClose} className="close-icon" />
                  <MenuItem
                    onClick={validateRequeue}
                    className={`before-divider-options-${isActive("Requeue")}`}
                    disabled={isActive("Requeue") === "inactive"}
                  >
                    <Typography variant="body1" style={{ flexGrow: 1 }}>
                      Requeue
                    </Typography>
                    <InfoIcon
                      style={{
                        marginLeft: "auto",
                        width: 16,
                        height: 16,
                        verticalAlign: "middle",
                      }}
                    />
                  </MenuItem>

                  <MenuItem
                    onClick={validateHold}
                    className={`before-divider-options-${isActive("Hold")}`}
                    disabled={rowData.isHeld || rowData.connectionPairId === -1}
                  >
                    <Typography>Hold</Typography>
                  </MenuItem>

                  <MenuItem
                    onClick={validateUnhold}
                    className={`before-divider-options-${isActive("Unhold")}`}
                    disabled={rowData.connectionPairId === -1}
                  >
                    <Typography>Unhold</Typography>
                  </MenuItem>

                  <MenuItem
                    onClick={validateProcess}
                    className={`before-divider-options-${isActive("Process")}`}
                    disabled={rowData.isHeld || rowData.connectionPairId === -1}
                  >
                    <Typography>Process</Typography>
                  </MenuItem>

                  <MenuItem
                    onClick={validateArchive}
                    className={`before-divider-options-${isActive("Archive")}`}
                    disabled={rowData.isHeld || rowData.connectionPairId === -1}
                  >
                    <Typography style={{ color: "var(--secondary-1)" }}>
                      Archive
                    </Typography>
                  </MenuItem>
                  <MenuItem
                    onClick={validateRestore}
                    className={`before-divider-options-${isActive("Restore")}`}
                    disabled={!rowData.isArchived || rowData.connectionPairId === -1} // This action can ONLY be used with Archived records
                  >
                    <Typography>Restore</Typography>
                  </MenuItem>
                  <Divider component="li" />
                  <MenuItem
                    onClick={handleViewSourceClick}
                    className={`after-divider-options-${isActive("View Data")}`}
                  >
                    <ListItemText
                      primary={"View Data"}
                      secondary={"(Raw, Structured & Catalog Data)"}
                    />
                  </MenuItem>

                  <MenuItem
                    onClick={handleWorkflowSettingsClick}
                    className={`after-divider-options-${isActive(
                      "Workflow Settings"
                    )}`}
                  >
                    <Typography variant="body1">Workflow Settings</Typography>
                  </MenuItem>
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Fade>
        )}
      </Popper>
    </>
  );
};

export default ThreeDotSettingMenu;
